import { Application, Container, FederatedPointerEvent, Point } from "pixi.js";
import { ViewportManager } from "./ViewportManager";
import type { ShapeManager } from "./ShapeManager";
import { ImageShape } from "./shapes/ImageShape";
import type { CommandManager } from "./CommandManager";
import type { ToolModeManager } from "./ToolModeManager";

export class InteractionManager {
    private app: Application;
    private viewportManager: ViewportManager;
    private shapeManager: ShapeManager;
    private commandManager: CommandManager;
    public container: Container;
    public isDragging: boolean = false;
    private lastPosition: Point | null = null;
    private lastPinchDistance: number = 0;
    private isCanvasDragging: boolean = false;
    private isSpacePanning: boolean = false;
    private isMiddleMousePanning: boolean = false;
    private isMarqueeSelecting: boolean = false;
    private marqueeStartWorld: { x: number; y: number } | null = null;
    private readonly toolModeManager: ToolModeManager;

    // Stored bound handlers — required for removeEventListener to match the registered ref
    private readonly _onPointerMove = (e: PointerEvent) => this.onGlobalPointerMove(e);
    private readonly _onKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
    private readonly _onKeyUp = (e: KeyboardEvent) => this.handleKeyUp(e);
    private readonly _onWheel = (e: WheelEvent) => this.onWheel(e);
    private readonly _onTouchStart = (e: TouchEvent) => this.onTouchStart(e);
    private readonly _onTouchMove = (e: TouchEvent) => this.onTouchMove(e);

    constructor(app: Application, container: Container, viewportManager: ViewportManager, shapeManager: ShapeManager, commandManager: CommandManager, toolModeManager: ToolModeManager) {
        this.app = app;
        this.container = container;
        this.commandManager = commandManager;
        this.viewportManager = viewportManager;
        this.shapeManager = shapeManager;
        this.toolModeManager = toolModeManager;
        this.shapeManager.setSelectionEnabled(this.toolModeManager.getMode() === 'select');
        this.toolModeManager.on('modeChanged', (mode) => {
            this.shapeManager.setSelectionEnabled(mode === 'select');
            document.body.style.cursor = mode === 'hand' ? 'grab' : 'default';
        });
    }

    public setup(): void {
        this.app.stage.eventMode = 'static';
        this.app.stage.hitArea = this.app.screen;

        window.addEventListener('pointermove', this._onPointerMove);

        this.app.stage.on('pointerdown', this.onDragStart.bind(this));
        this.app.stage.on('pointerup', this.onDragEnd.bind(this));

        this.setupZoomEvents();
        this.setupWheelEvents();
        this.setupKeyboardEvents();
    }

    private setupKeyboardEvents(): void {
        document.addEventListener('keydown', this._onKeyDown, { capture: true, passive: false });
        document.addEventListener('keyup', this._onKeyUp, { capture: true, passive: false });
        document.addEventListener('paste', this.handlePaste);
    }

    private handlePaste = (event: ClipboardEvent) => {
        event.preventDefault();

        const items = event.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf('image') !== -1) {
                const file = item.getAsFile();
                if (file) {
                    this.handleImagePaste(file);
                }
            }
        }
    };

    private handleImagePaste(file: File): void {
        const reader = new FileReader();
        reader.onload = (e) => {
            // Create image shape and add to canvas
            this.createImageFromPaste(e.target?.result as string);
        };
        reader.readAsDataURL(file);
    }

    private handleKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Space' && !this.isTypingTarget(event.target)) {
            event.preventDefault();
            this.isSpacePanning = true;
            document.body.style.cursor = 'grab';
            return;
        }

        if (event.key.toLowerCase() === 't' && !event.ctrlKey && !event.metaKey && !event.altKey && !this.isTypingTarget(event.target)) {
            event.preventDefault();
            this.shapeManager.toggleTransformMode();
            return;
        }

        if (event.key === 'Escape') {
            this.shapeManager.deselectCurrentShape();
        }

        if (event.key === 'Backspace') {
            const selectedShapes = this.shapeManager.getSelectedShapes();
            if (selectedShapes.length > 1) {
                this.shapeManager.removeSelectedShapes();
                return;
            }
            const selectedShape = this.shapeManager.getSelectedShape();
            if (selectedShape) {
                this.shapeManager.removeShape(selectedShape);
            }
        }

        // Handle both Ctrl (Windows/Linux) and Cmd (Mac)
        const isUndo = (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey;
        const isRedo = (event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey;

        if (isUndo) {
            event.preventDefault();
            this.commandManager.undo();
        }

        if (isRedo) {
            event.preventDefault();
            this.commandManager.redo();
        }
    }

    private handleKeyUp(event: KeyboardEvent): void {
        if (event.code === 'Space') {
            this.isSpacePanning = false;
            if (!this.isCanvasDragging) {
                document.body.style.cursor = this.toolModeManager.getMode() === 'hand' ? 'grab' : 'default';
            }
        }
    }

    private isTypingTarget(target: EventTarget | null): boolean {
        return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
    }

    private onGlobalPointerMove(event: PointerEvent): void {
        if (!this.lastPosition) {
            // Update cursor based on hover state
            const selectedShape = this.shapeManager.getSelectedShape();
            if (!selectedShape && !this.isDragging) {
                document.body.style.cursor = 'grab';
            }
            return;
        }

        const dx = event.clientX - this.lastPosition.x;
        const dy = event.clientY - this.lastPosition.y;

        if (this.isCanvasDragging) {
            this.viewportManager.pan(dx, dy);
            // Maintain grabbing cursor while dragging
            document.body.style.cursor = 'grabbing';
        } else if (this.isMarqueeSelecting && this.marqueeStartWorld) {
            const canvas = this.viewportManager.getCanvas();
            const rect = canvas.getBoundingClientRect();
            const current = this.viewportManager.screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
            this.shapeManager.drawMarquee(this.marqueeStartWorld, current);
        } else {
            this.shapeManager.updateGroupTransformFromPointer(event);
        }

        this.lastPosition = new Point(event.clientX, event.clientY);
    }

    public cleanup(): void {
        this.app.stage.removeAllListeners();
        window.removeEventListener('pointermove', this._onPointerMove);
        document.removeEventListener('keydown', this._onKeyDown, { capture: true });
        document.removeEventListener('keyup', this._onKeyUp, { capture: true });
        document.removeEventListener('paste', this.handlePaste);
        this.app.canvas.removeEventListener('wheel', this._onWheel);
        this.app.canvas.removeEventListener('touchstart', this._onTouchStart);
        this.app.canvas.removeEventListener('touchmove', this._onTouchMove);
    }

    private setupZoomEvents(): void {
        this.app.canvas.addEventListener('touchstart', this._onTouchStart, {passive: true});
        this.app.canvas.addEventListener('touchmove', this._onTouchMove, {passive: true});
    }

    private setupWheelEvents(): void {
        this.app.canvas.addEventListener('wheel', this._onWheel, {passive: false});
    }

    private onDragStart(event: FederatedPointerEvent): void {
        this.isDragging = true;
        this.lastPosition = new Point(event.globalX, event.globalY);
        this.isMiddleMousePanning = (event as any).button === 1 || (event as any).buttons === 4;
        const forcePan = this.toolModeManager.getMode() === 'hand' || this.isSpacePanning || this.isMiddleMousePanning;
        const targetIsCanvas = event.target === this.container || event.target === this.container.parent || event.target === this.app.stage;

        if (forcePan) {
            this.isCanvasDragging = true;
            document.body.style.cursor = 'grabbing';
            return;
        }

        if (event.shiftKey && targetIsCanvas) {
            const canvas = this.viewportManager.getCanvas();
            const rect = canvas.getBoundingClientRect();
            this.marqueeStartWorld = this.viewportManager.screenToWorld(event.global.x - rect.left, event.global.y - rect.top);
            this.isMarqueeSelecting = true;
            this.shapeManager.drawMarquee(this.marqueeStartWorld, this.marqueeStartWorld);
            return;
        }

        if (this.shapeManager.handleGroupPointerDown(event)) {
            return;
        }

        if (this.shapeManager.startSelectedGroupDragFromPointer(event)) {
            return;
        }

        // Handle canvas clicks for deselection and handles
        this.shapeManager.handleCanvasClick(event);

        // Only start dragging if we're not resizing
        // The ShapeManager will handle both drag and resize detection
        const selectedShape = this.shapeManager.getSelectedShape();
        if (selectedShape && this.shapeManager.getSelectedShapes().length === 1 && !selectedShape.isResizing) {
            this.shapeManager.startDragSelectedShape(event);
        }

        // If no shape is selected, enable canvas dragging
        if (!selectedShape) {
            this.isCanvasDragging = true;
            document.body.style.cursor = 'grabbing';
        }
    }

    private onDragEnd(event?: FederatedPointerEvent): void {
        const lastPosition = this.lastPosition;
        this.isDragging = false;
        this.isCanvasDragging = false;
        this.isMiddleMousePanning = false;
        this.lastPosition = null;
        document.body.style.cursor = 'default';
        if (this.isMarqueeSelecting && this.marqueeStartWorld) {
            const canvas = this.viewportManager.getCanvas();
            const rect = canvas.getBoundingClientRect();
            const globalX = event?.global?.x ?? lastPosition?.x ?? 0;
            const globalY = event?.global?.y ?? lastPosition?.y ?? 0;
            const current = this.viewportManager.screenToWorld(globalX - rect.left, globalY - rect.top);
            this.shapeManager.selectShapesInRect(this.marqueeStartWorld, current, false);
            this.shapeManager.clearMarquee();
        }
        this.isMarqueeSelecting = false;
        this.marqueeStartWorld = null;

        // End shape dragging, resizing, and rotating
        this.shapeManager.endDrag();
        this.shapeManager.endResize();
        this.shapeManager.endRotate();
        this.shapeManager.endGroupTransform();
    }

    private onWheel(event: WheelEvent): void {
        event.preventDefault();

        const zoom = 1 - event.deltaY * 0.001;
        const center = new Point(event.clientX, event.clientY);

        this.viewportManager.zoom(zoom, center);
    }

    private onTouchStart(event: TouchEvent): void {
        if (event.touches.length === 2) {
            this.lastPinchDistance = this.getPinchDistance(event);
        }
    }

    private onTouchMove(event: TouchEvent): void {
        if (event.touches.length === 2) {
            const currentDistance = this.getPinchDistance(event);
            const scale = currentDistance / this.lastPinchDistance;
            this.lastPinchDistance = currentDistance;

            const center = new Point((event.touches[0].clientX + event.touches[1].clientX) / 2, (event.touches[0].clientY + event.touches[1].clientY) / 2);

            this.viewportManager.zoom(scale, center);
        }
    }

    private getPinchDistance(event: TouchEvent): number {
        return Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY);
    }

    private createImageFromPaste(imageDataUrl: string): void {
        const imageShape = new ImageShape(imageDataUrl);

        // Position at viewport center
        const bounds = this.viewportManager.getVisibleBounds();
        const centerX = bounds.left + (bounds.right - bounds.left) / 2;
        const centerY = bounds.top + (bounds.bottom - bounds.top) / 2;

        imageShape.position(centerX, centerY);
        this.shapeManager.addShape(imageShape);
    }
}
